import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useCombobox, autocomplete } from "@szhsin/react-autocomplete";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import Loader from "./Loader";

const Autocomplete = ({
  data,
  onSelect,
  placeholder,
  initValue,
  onChange,
  disabled,
  resetOnSelect,
  className,
  value: controlledValue,
  inputClassName = "",
  inputProps = {},
  listClassName = "",
  hideClear,
  hideCaret,
  // When true, skip the built-in label-substring filter and show `data` as-is.
  // Use for fields whose parent already filters the list (e.g. server- or
  // matcher-driven search) so the naive substring match doesn't re-hide rows.
  disableLocalFilter = false,
  // When true, the open dropdown shows a spinner row instead of items or
  // "No results" — for async-backed fields, so an in-flight search never
  // flashes a misleading empty state.
  loading = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initValue ?? controlledValue ?? "");
  const [selected, setSelected] = useState(initValue ?? controlledValue);
  const isFirstRender = useRef(true);
  const containerRef = useRef(null);
  const [listPosition, setListPosition] = useState(() => {
    const rect = containerRef.current?.getBoundingClientRect();

    return { top: rect?.bottom ?? 0, left: rect?.left ?? 0, width: rect?.width ?? 0 };
  });

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue ?? "");
      // NOTE: only set selected on first render to set the initial state.
      // Don't update selected on next controlled value changes (during typing)
      if (isFirstRender.current && !resetOnSelect) {
        setSelected(controlledValue);
      }
      isFirstRender.current = false;
      return;
    }

    if (initValue !== undefined) {
      setValue(initValue ?? "");
      setSelected(initValue);
    }
    isFirstRender.current = false;
  }, [controlledValue, initValue, resetOnSelect]);

  useEffect(() => {
    if (initValue === null) {
      setValue(initValue);
      setSelected(initValue);
    }
  }, [initValue]);

  // Filter items only when user is typing (value differs from selected) or when no value is set
  // This allows showing all options when dropdown opens with a pre-selected value.
  // disableLocalFilter lets the parent own filtering entirely (see prop comment).
  const items =
    !disableLocalFilter && value && value !== selected
      ? data.filter((item) => item.label?.toLowerCase().indexOf(value?.toLowerCase()) !== -1)
      : data;

  const onSelectChange = (option) => {
    onSelect(option);

    if (resetOnSelect) {
      setValue("");
      setSelected("");
      return;
    }

    if (!option) {
      setSelected("");
      return;
    }

    const nextLabel = option.label ?? option.key ?? "";

    setValue(nextLabel);
    setSelected(nextLabel);
  };

  const onChangeFn = (nextValue) => {
    if (nextValue === undefined) {
      if (onChange) onChange(value);
      return;
    }

    setValue(nextValue);
    if (selected && nextValue !== selected) {
      setSelected("");
    }
    if (onChange) onChange(nextValue);
  };

  const { getInputProps, getClearProps, getToggleProps, getListProps, getItemProps, open, focusIndex, isInputEmpty } =
    useCombobox({
      items,
      value,
      onChange: onChangeFn,
      selected,
      onSelectChange,
      feature: autocomplete({
        // The `select` option controls autocomplete in free or select mode
        select: true, // or false
        // Other options: rovingText, deselectOnClear, deselectOnChange, closeOnSelect
      }),
    });

  useLayoutEffect(() => {
    if (open && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setListPosition({
            top: rect.bottom,
            left: rect.left,
            width: rect.width,
          });
        }
      };

      updatePosition();

      // Update position on scroll and resize
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);

      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [open]);

  useEffect(() => {
    if (open && onChange) {
      onChange(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const defaultInputProps = getInputProps();

  const {
    onChange: userOnChange,
    onBlur: userOnBlur,
    onKeyDown: userOnKeyDown,
    onBeforeInput: userOnBeforeInput,
    onPaste: userOnPaste,
    ref: userRef,
    ...restInputProps
  } = inputProps;

  const composeHandlers = (userHandler, defaultHandler) => {
    if (userHandler && defaultHandler) {
      return (...args) => {
        userHandler(...args);
        if (!args[0]?.defaultPrevented) {
          defaultHandler(...args);
        }
      };
    }
    return userHandler || defaultHandler;
  };

  const mergedRef =
    userRef || defaultInputProps.ref
      ? (node) => {
          const defaultRef = defaultInputProps.ref;
          if (typeof defaultRef === "function") {
            defaultRef(node);
          } else if (defaultRef) {
            defaultRef.current = node;
          }

          if (typeof userRef === "function") {
            userRef(node);
          } else if (userRef) {
            userRef.current = node;
          }
        }
      : undefined;

  const mergedInputProps = {
    ...defaultInputProps,
    ...restInputProps,
    onChange: composeHandlers(userOnChange, defaultInputProps.onChange),
    onBlur: composeHandlers(userOnBlur, defaultInputProps.onBlur),
    onKeyDown: composeHandlers(userOnKeyDown, defaultInputProps.onKeyDown),
    onBeforeInput: composeHandlers(userOnBeforeInput, defaultInputProps.onBeforeInput),
    onPaste: composeHandlers(userOnPaste, defaultInputProps.onPaste),
    ref: mergedRef ?? defaultInputProps.ref,
  };

  return (
    <div className={classNames("autocomplete-wrapper", className)} ref={containerRef}>
      <div className="autocomplete-container">
        <input
          disabled={disabled}
          className={classNames("autocomplete-input", inputClassName)}
          placeholder={placeholder}
          {...mergedInputProps}
          style={{
            borderRadius: open ? "10px 10px 0px 0px" : "10px",
          }}
        />
        {!isInputEmpty && !disabled && !hideClear && (
          <button disabled={disabled} className="autocomplete-clear-btn" {...getClearProps()}></button>
        )}
        {!disabled && !hideCaret && (
          <button disabled={disabled} className="autocomplete-caret-btn" {...getToggleProps()}></button>
        )}
      </div>

      {open &&
        createPortal(
          <ul
            className={classNames("autocomplete-list", listClassName)}
            {...getListProps()}
            style={{
              display: "block",
              position: "fixed",
              top: `${listPosition.top}px`,
              left: `${listPosition.left}px`,
              maxWidth: `${listPosition.width - 10}px`,
              zIndex: 2001,
            }}
          >
            {loading ? (
              <li className="autocomplete-item" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Loader id="autocomplete-search-loader" />
                {t("common.searching")}
              </li>
            ) : items.length ? (
              items.map((item, index) => (
                <li
                  className={"autocomplete-item" + (focusIndex === index ? " is-focused" : "")}
                  style={{
                    textDecoration: selected === item.key ? "underline" : "none",
                  }}
                  key={item.key}
                  {...getItemProps({ item: item, index })}
                >
                  {item.label}
                </li>
              ))
            ) : (
              <li className="autocomplete-item">
                <b>{t("common.noResults")}</b>
              </li>
            )}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default Autocomplete;
